import * as React from "react";
import { RotateCcw } from "react-feather";
import type { TimeInputProps } from "../types";
import { parseTimeInput } from "../utils/parser/timeParser";
import "./TimeInput.css";

// =============================================================================
// TimeInput Component
// Time-only input with scroll wheel support and undo functionality
// =============================================================================

type TimeGranularity = "hour" | "minute" | "second";

export const TimeInput = React.memo(function TimeInput({
  value,
  onChange,
  onBlur,
  onEnter,
  granularity = "minute",
  isError = false,
  disabled = false,
  label,
  showScrollOverlay = false,
  previousValue,
  onUndo,
  className,
}: TimeInputProps) {
  const [internalValue, setInternalValue] = React.useState(value);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const scrollAccumulatorRef = React.useRef<{ hour: number; minute: number; second: number }>({
    hour: 0,
    minute: 0,
    second: 0,
  });

  // Sync internal value with prop when prop changes externally
  React.useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // ---------------------------------------------------------------------------
  // Computed Values
  // ---------------------------------------------------------------------------

  const timeFormat = getTimeFormat(granularity);
  const fullLabel = label ? `${label} ${timeFormat.labelSuffix}` : null;
  const segments = parseTimeSegments(internalValue, granularity);
  const showUndoButton = isError && previousValue != null && previousValue !== internalValue && onUndo;

  // ---------------------------------------------------------------------------
  // Event Handlers
  // ---------------------------------------------------------------------------

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      setInternalValue(e.target.value);
      onChange(e.target.value);
    },
    [disabled, onChange]
  );

  const handleBlur = React.useCallback(() => {
    if (disabled) return;
    const parsed = parseTimeInput(internalValue, granularity);
    onBlur?.(parsed.value, parsed.isValid);
  }, [disabled, granularity, onBlur, internalValue]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return;

      if (e.key === "Enter") {
        e.preventDefault();
        const parsed = parseTimeInput(internalValue, granularity);
        onBlur?.(parsed.value, parsed.isValid);
        if (parsed.isValid) {
          onEnter?.();
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        setInternalValue("");
        onChange("");
        onBlur?.("", true);
      }
    },
    [disabled, granularity, onChange, onBlur, onEnter, internalValue]
  );

  const handleSegmentScroll = React.useCallback(
    (e: React.WheelEvent, segment: "hour" | "minute" | "second") => {
      if (disabled) return;
      e.preventDefault();

      // Accumulate scroll delta - require threshold before changing value
      const SCROLL_THRESHOLD = 50;
      scrollAccumulatorRef.current[segment] += e.deltaY;

      // Check if threshold reached
      if (Math.abs(scrollAccumulatorRef.current[segment]) < SCROLL_THRESHOLD) {
        return;
      }

      // Determine direction and reset accumulator
      const delta = scrollAccumulatorRef.current[segment] > 0 ? -1 : 1; // Scroll up = increment
      scrollAccumulatorRef.current[segment] = 0;

      const parts = internalValue.split(":").map(Number);
      let hours = parts[0] || 0;
      let minutes = parts[1] || 0;
      let seconds = parts[2] || 0;

      switch (segment) {
        case "hour":
          hours = (hours + delta + 24) % 24;
          break;
        case "minute":
          // Don't allow scrolling minutes when granularity is hour
          if (granularity === "hour") return;
          minutes = (minutes + delta + 60) % 60;
          break;
        case "second":
          seconds = (seconds + delta + 60) % 60;
          break;
      }

      // Format based on granularity - enforce 00 for lower granularities
      let newTime: string;
      if (granularity === "hour") {
        newTime = `${String(hours).padStart(2, "0")}:00`;
      } else if (granularity === "second") {
        newTime = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
      } else {
        newTime = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
      }

      setInternalValue(newTime);
      onChange(newTime);
      onBlur?.(newTime, true); // Scroll changes always produce valid time
    },
    [disabled, internalValue, granularity, onChange, onBlur]
  );

  const handleOverlayClick = React.useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const handleUndo = React.useCallback(() => {
    onUndo?.();
  }, [onUndo]);

  // ---------------------------------------------------------------------------
  // Computed Classes
  // ---------------------------------------------------------------------------

  const containerClasses = [
    "time-input",
    isError && "time-input--error",
    disabled && "time-input--disabled",
    showScrollOverlay && "time-input--with-overlay",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className={containerClasses}>
      {fullLabel && <label className="time-input__label">{fullLabel}</label>}
      <div className="time-input__wrapper">
        <input
          ref={inputRef}
          type="text"
          className={`time-input__field${showUndoButton ? " time-input__field--with-undo" : ""}`}
          value={internalValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={timeFormat.placeholder}
          disabled={disabled}
          aria-invalid={isError}
        />

        {/* Undo button - shown when time is invalid and previous value exists */}
        <button
          type="button"
          className={`time-input__undo${showUndoButton ? " time-input__undo--visible" : ""}`}
          onClick={handleUndo}
          aria-label="Undo time"
          aria-hidden={!showUndoButton}
          tabIndex={showUndoButton ? 0 : -1}
        >
          <RotateCcw size={12} />
        </button>

        {/* Scroll overlay - shown when enabled */}
        {showScrollOverlay && (
          <>
            {/* Tooltip showing current value on hover */}
            <div className="time-input__tooltip">{internalValue || timeFormat.placeholder}</div>

            {/* Scroll overlay - appears on hover */}
            <div className="time-input__overlay" onClick={handleOverlayClick}>
              <span
                className="time-input__segment time-input__segment--number"
                onWheel={(e) => handleSegmentScroll(e, "hour")}
              >
                {segments.hours}
              </span>
              {/* Minutes - shown always, but scroll disabled when granularity is "hour" */}
              <span className="time-input__segment time-input__segment--colon">:</span>
              <span
                className={`time-input__segment time-input__segment--number${granularity === "hour" ? " time-input__segment--disabled" : ""}`}
                onWheel={(e) => handleSegmentScroll(e, "minute")}
              >
                {granularity === "hour" ? "00" : segments.minutes}
              </span>
              {/* Seconds - only shown when granularity is "second" */}
              {granularity === "second" && (
                <>
                  <span className="time-input__segment time-input__segment--colon">:</span>
                  <span
                    className="time-input__segment time-input__segment--number"
                    onWheel={(e) => handleSegmentScroll(e, "second")}
                  >
                    {segments.seconds}
                  </span>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
});

// =============================================================================
// Helper Functions
// =============================================================================

function getTimeFormat(granularity: TimeGranularity): { labelSuffix: string; placeholder: string } {
  switch (granularity) {
    case "hour":
      return { labelSuffix: "(HH)", placeholder: "HH" };
    case "second":
      return { labelSuffix: "(HH:mm:ss)", placeholder: "HH:mm:ss" };
    case "minute":
    default:
      return { labelSuffix: "(HH:mm)", placeholder: "HH:mm" };
  }
}

function parseTimeSegments(
  timeValue: string,
  granularity: TimeGranularity
): {
  hours: string;
  minutes: string;
  seconds?: string;
} {
  const parts = timeValue.split(":");
  return {
    hours: parts[0]?.padStart(2, "0") || "00",
    minutes: parts[1]?.padStart(2, "0") || "00",
    seconds: granularity === "second" ? parts[2]?.padStart(2, "0") || "00" : undefined,
  };
}
